export type AppId = 'about' | 'feed' | 'clock' | 'works' | 'archive' | 'lab' | 'mov' | 'notes' | 'settings';

export interface Point {
    x: number;
    y: number;
}

export interface Size {
    width: number;
    height: number;
}

export type Rect = Point & Size;

export interface WindowState {
    /** 열린 패널 목록. 뒤로 갈수록 위에 있다. */
    stack: AppId[];
    rects: Partial<Record<AppId, Rect>>;
    /** 사람이 옮긴 패널. 화면 크기가 바뀌어도 자리를 유지한다. */
    moved: AppId[];
    /** 사람이 크기를 바꾼 패널. 화면 크기가 바뀌어도 크기를 유지한다. */
    resized: AppId[];
}

export type WindowAction =
    | { type: 'layout'; state: WindowState }
    | { type: 'resize'; rects: WindowState['rects'] }
    | { type: 'open'; id: AppId; exclusive?: boolean }
    | { type: 'focus'; id: AppId }
    | { type: 'close'; id: AppId }
    | { type: 'move'; id: AppId; position: Point }
    | { type: 'place'; id: AppId; rect: Rect }
    | { type: 'drift'; positions: Partial<Record<AppId, Point>> }
    | { type: 'tile'; rects: WindowState['rects'] };

export type ResizeEdge = 'n' | 'e' | 's' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export const COMPACT_WIDTH = 640;
/** CSS와 같은 기준으로 좁은 화면을 판단한다. 패널 영역 폭으로 재면 뷰포트 641~711px에서 어긋난다 */
export const COMPACT_QUERY = `(max-width: ${COMPACT_WIDTH}px)`;

/** 내용이 무너지지 않는 패널별 최소 크기 */
export const MIN_SIZES: Record<AppId, Size> = {
    about: { width: 320, height: 220 },
    feed: { width: 280, height: 220 },
    clock: { width: 300, height: 130 },
    works: { width: 260, height: 396 },
    archive: { width: 320, height: 300 },
    lab: { width: 130, height: 130 },
    mov: { width: 280, height: 170 },
    notes: { width: 130, height: 150 },
    settings: { width: 340, height: 480 },
};

const GAP = 16;
const TWO_COLUMN_WIDTH = 1130;
const MAX_GROUP: Size = { width: 1480, height: 820 };
const SETTINGS: Size = { width: 400, height: 560 };
const ARCHIVE: Size = { width: 640, height: 480 };
const GRID_COLUMNS = 12;
const GRID_ROWS = 8;
const TILE_ATTEMPTS = 5000;

export const clampPosition = (position: Point, size: Size, bounds: Size): Point => ({
    x: Math.max(0, Math.min(position.x, bounds.width - size.width)),
    y: Math.max(0, Math.min(position.y, bounds.height - size.height)),
});

/** 끄는 가장자리만 움직인다. 최소 크기와 패널 영역을 넘지 않는다 */
export const resizeRect = (start: Rect, edge: ResizeEdge, delta: Point, min: Size, bounds: Size): Rect => {
    let left = start.x;
    let top = start.y;
    let right = start.x + start.width;
    let bottom = start.y + start.height;

    if (edge.includes('w')) left = Math.max(0, Math.min(left + delta.x, right - min.width));
    if (edge.includes('e')) right = Math.min(bounds.width, Math.max(right + delta.x, left + min.width));
    if (edge.includes('n')) top = Math.max(0, Math.min(top + delta.y, bottom - min.height));
    if (edge.includes('s')) bottom = Math.min(bounds.height, Math.max(bottom + delta.y, top + min.height));

    return { x: left, y: top, width: right - left, height: bottom - top };
};

interface GridArea {
    column: number;
    row: number;
    columns: number;
    rows: number;
}

/**
 * 패널을 12×8 그리드에 빈틈없이 나눠 담는다. 영역을 무작위로 둘씩 쪼개므로 부를 때마다 배치가 달라진다.
 * 최소 크기를 지키는 배치를 찾지 못하면 고른 격자로 나눈다.
 */
export const tileLayout = (ids: AppId[], bounds: Size, random: () => number = Math.random): WindowState['rects'] => {
    if (ids.length === 0) return {};

    const width = Math.min(bounds.width, MAX_GROUP.width);
    const height = Math.min(bounds.height, MAX_GROUP.height);
    const left = (bounds.width - width) / 2;
    const top = (bounds.height - height) / 2;
    const cellWidth = (width - GAP * (GRID_COLUMNS - 1)) / GRID_COLUMNS;
    const cellHeight = (height - GAP * (GRID_ROWS - 1)) / GRID_ROWS;

    const span = (length: number, cell: number) => Math.ceil((length + GAP) / (cell + GAP));
    const minColumns = (id: AppId) => span(MIN_SIZES[id].width, cellWidth);
    const minRows = (id: AppId) => span(MIN_SIZES[id].height, cellHeight);
    const fits = (id: AppId, area: GridArea) => area.columns >= minColumns(id) && area.rows >= minRows(id);

    const shuffled = (list: AppId[]) => list
        .map((id) => ({ id, order: random() }))
        .sort((a, b) => a.order - b.order)
        .map(({ id }) => id);

    const split = (group: AppId[], area: GridArea): [AppId, GridArea][] | null => {
        if (group.length === 1) return fits(group[0], area) ? [[group[0], area]] : null;

        const mixed = shuffled(group);
        const count = 1 + Math.floor(random() * (group.length - 1));
        const isWide = area.columns * cellWidth >= area.rows * cellHeight;
        const sideBySide = random() < (isWide ? 0.8 : 0.2);
        const length = sideBySide ? area.columns : area.rows;
        const groupA = mixed.slice(0, count);
        const groupB = mixed.slice(count);
        const minSpan = sideBySide ? minColumns : minRows;

        // 양쪽 패널이 모두 들어가는 범위에서만 자른다
        const low = Math.max(1, ...groupA.map(minSpan));
        const high = length - Math.max(1, ...groupB.map(minSpan));
        if (low > high) return null;

        // 패널 수에 비례해 자르고, 한두 칸 흔들어 매번 다른 비율을 만든다
        const proportional = Math.round(length * (count / group.length));
        const cut = Math.max(low, Math.min(high, proportional + Math.floor(random() * 5) - 2));
        const first = sideBySide ? { ...area, columns: cut } : { ...area, rows: cut };
        const second = sideBySide
            ? { ...area, column: area.column + cut, columns: area.columns - cut }
            : { ...area, row: area.row + cut, rows: area.rows - cut };

        const placedFirst = split(groupA, first);
        const placedSecond = placedFirst && split(groupB, second);
        return placedFirst && placedSecond ? [...placedFirst, ...placedSecond] : null;
    };

    const evenly = (): [AppId, GridArea][] => {
        const columns = Math.ceil(Math.sqrt(ids.length));
        const rows = Math.ceil(ids.length / columns);

        return shuffled(ids).map((id, index) => {
            const column = index % columns;
            const row = Math.floor(index / columns);
            const startColumn = Math.floor((column * GRID_COLUMNS) / columns);
            const startRow = Math.floor((row * GRID_ROWS) / rows);

            return [id, {
                column: startColumn,
                row: startRow,
                columns: Math.floor(((column + 1) * GRID_COLUMNS) / columns) - startColumn,
                rows: Math.floor(((row + 1) * GRID_ROWS) / rows) - startRow,
            }];
        });
    };

    const whole = { column: 0, row: 0, columns: GRID_COLUMNS, rows: GRID_ROWS };
    let placed: [AppId, GridArea][] | null = null;
    for (let attempt = 0; attempt < TILE_ATTEMPTS && !placed; attempt += 1) {
        placed = split(ids, whole);
    }

    const rects: WindowState['rects'] = {};
    (placed ?? evenly()).forEach(([id, area]) => {
        rects[id] = {
            x: Math.round(left + area.column * (cellWidth + GAP)),
            y: Math.round(top + area.row * (cellHeight + GAP)),
            width: Math.round(area.columns * cellWidth + (area.columns - 1) * GAP),
            height: Math.round(area.rows * cellHeight + (area.rows - 1) * GAP),
        };
    });
    return rects;
};

const centered = (size: Size, bounds: Size): Rect => {
    const width = Math.min(size.width, bounds.width);
    const height = Math.min(size.height, bounds.height);

    return {
        x: Math.round((bounds.width - width) / 2),
        y: Math.round((bounds.height - height) / 2),
        width,
        height,
    };
};

/** 눌러서 펼친 패널이 차지하는 자리. 패널 묶음이 놓이는 영역과 같다 */
export const expandedRect = (bounds: Size): Rect => centered(MAX_GROUP, bounds);

/** 작품 비율을 지키되 남은 높이와 최소 크기 안에 맞춘다 */
const movHeight = (width: number, room: number) => Math.max(MIN_SIZES.mov.height, Math.min(Math.round((width * 9) / 16), room));

/** 기본 배치: 왼쪽 열에 소개와 극장, 오른쪽 열에 시계, 작업, 책장, 실험, 메모 */
export const defaultLayout = (bounds: Size, compact = bounds.width <= COMPACT_WIDTH): WindowState => {
    if (compact) {
        return { stack: ['about'], rects: {}, moved: [], resized: [] };
    }

    const settings = centered(SETTINGS, bounds);
    const archive = centered(ARCHIVE, bounds);

    if (bounds.width < TWO_COLUMN_WIDTH) {
        const about = { width: Math.max(Math.round(bounds.width * 0.5), MIN_SIZES.about.width), height: 320 };
        const works = { width: Math.max(Math.round(bounds.width * 0.42), MIN_SIZES.works.width), height: 440 };

        // 작업 왼쪽, 소개 위 빈자리에 극장을 둔다
        const movWidth = Math.max(bounds.width - works.width - GAP, MIN_SIZES.mov.width);

        return {
            stack: ['works', 'mov', 'about'],
            rects: {
                about: { x: 0, y: bounds.height - Math.min(about.height, bounds.height), ...about },
                works: { x: bounds.width - works.width, y: 0, ...works },
                feed: centered({ width: 560, height: 360 }, bounds),
                clock: centered({ width: 520, height: 200 }, bounds),
                lab: centered({ width: 240, height: 240 }, bounds),
                mov: { x: 0, y: 0, width: movWidth, height: movHeight(movWidth, bounds.height - about.height - GAP) },
                notes: centered({ width: 240, height: 240 }, bounds),
                archive,
                settings,
            },
            moved: [],
            resized: [],
        };
    }

    const width = Math.min(bounds.width, MAX_GROUP.width);
    const height = Math.min(bounds.height, MAX_GROUP.height);
    const left = Math.round((bounds.width - width) / 2);
    const top = Math.round((bounds.height - height) / 2);

    const column = Math.floor((width - GAP) / 2);
    const right = left + column + GAP;
    const aboutHeight = Math.round((height - GAP) * 0.46);
    const clockHeight = Math.round((height - GAP) * 0.31);
    const lowerTop = top + clockHeight + GAP;
    const lowerHeight = height - clockHeight - GAP;
    const worksWidth = Math.max(Math.round(column * 0.4), MIN_SIZES.works.width);
    const sideLeft = right + worksWidth + GAP;
    const sideWidth = column - worksWidth - GAP;
    const feedHeight = Math.min(
        Math.max(Math.round(lowerHeight * 0.55), MIN_SIZES.feed.height),
        lowerHeight - GAP - MIN_SIZES.lab.height,
    );
    const smallTop = lowerTop + feedHeight + GAP;
    const smallHeight = lowerHeight - feedHeight - GAP;
    const labWidth = Math.floor((sideWidth - GAP) / 2);

    return {
        stack: ['mov', 'clock', 'works', 'feed', 'lab', 'notes', 'about'],
        rects: {
            about: { x: left, y: top, width: column, height: aboutHeight },
            mov: {
                x: left,
                y: top + aboutHeight + GAP,
                width: column,
                height: height - aboutHeight - GAP,
            },
            clock: { x: right, y: top, width: column, height: clockHeight },
            works: { x: right, y: lowerTop, width: worksWidth, height: lowerHeight },
            feed: { x: sideLeft, y: lowerTop, width: sideWidth, height: feedHeight },
            lab: { x: sideLeft, y: smallTop, width: labWidth, height: smallHeight },
            notes: {
                x: sideLeft + labWidth + GAP,
                y: smallTop,
                width: sideWidth - labWidth - GAP,
                height: smallHeight,
            },
            archive,
            settings,
        },
        moved: [],
        resized: [],
    };
};

export const windowReducer = (state: WindowState, action: WindowAction): WindowState => {
    switch (action.type) {
        case 'layout':
            return action.state;
        case 'resize': {
            const rects = { ...action.rects };
            state.moved.forEach((id) => {
                const previous = state.rects[id];
                const next = rects[id];
                if (previous && next) rects[id] = { ...next, x: previous.x, y: previous.y };
            });
            state.resized.forEach((id) => {
                const previous = state.rects[id];
                if (previous) rects[id] = previous;
            });
            return { ...state, rects };
        }
        case 'open': {
            const others = action.exclusive ? [] : state.stack.filter((id) => id !== action.id);
            return { ...state, stack: [...others, action.id] };
        }
        case 'focus':
            if (state.stack[state.stack.length - 1] === action.id) return state;
            return { ...state, stack: [...state.stack.filter((id) => id !== action.id), action.id] };
        case 'close':
            return { ...state, stack: state.stack.filter((id) => id !== action.id) };
        case 'move': {
            const rect = state.rects[action.id];
            if (!rect) return state;
            return {
                ...state,
                rects: { ...state.rects, [action.id]: { ...rect, ...action.position } },
                moved: state.moved.includes(action.id) ? state.moved : [...state.moved, action.id],
            };
        }
        case 'place':
            return {
                ...state,
                rects: { ...state.rects, [action.id]: action.rect },
                resized: state.resized.includes(action.id) ? state.resized : [...state.resized, action.id],
            };
        case 'drift': {
            const rects = { ...state.rects };
            (Object.keys(action.positions) as AppId[]).forEach((id) => {
                const rect = rects[id];
                if (rect) rects[id] = { ...rect, ...action.positions[id] };
            });
            return { ...state, rects };
        }
        case 'tile': {
            const ids = Object.keys(action.rects) as AppId[];
            return {
                ...state,
                rects: { ...state.rects, ...action.rects },
                moved: Array.from(new Set([...state.moved, ...ids])),
                resized: Array.from(new Set([...state.resized, ...ids])),
            };
        }
        default:
            return state;
    }
};
